import { alpha } from '@mui/material/styles';
import { PropsWithChildren } from 'react';
import { LoadingDotsCenter } from '../animated-foxes';
import { Backdrop } from '../backdrop';

/**
 * A container component that manages loading UI with two display modes.
 *
 * @remarks
 * When `dontRenderChildren` and `loading` is true, displays centered loading dots without rendering children.
 * When `dontRenderChildren` and `loading` is true, renders children with a semi-transparent backdrop overlay containing loading dots.
 *
 * The backdrop uses the theme's background color with 60% opacity, should work for both light and dark modes.
 *
 * @example
 * ```tsx
 * function MyDataTable() {
 *   const { data, loading } = useMyDataQuery();
 *
 *   return (
 *     <LoadingContainer
 *       loading={loading}
 *       dontRenderChildren={!data}
 *     >
 *       <DataTable data={data} />
 *     </LoadingContainer>
 *   );
 * }
 * ```
 */
export function LoadingContainer({
	loading,
	children,
	dontRenderChildren,
	backdropZindex
}: PropsWithChildren<PropsWithChildren<{ loading: boolean; dontRenderChildren?: boolean; backdropZindex?: number }>>) {
	if (loading && dontRenderChildren) {
		return <LoadingDotsCenter />;
	}

	if (dontRenderChildren) {
		return null;
	}

	return (
		<div style={{ position: 'relative' }}>
			<Backdrop
				sx={theme => ({
					position: 'absolute',
					backgroundColor: alpha(theme.palette.background.default, 0.6),
					zIndex: backdropZindex ?? 2
				})}
				open={!!loading}
			>
				<LoadingDotsCenter />
			</Backdrop>
			{children}
		</div>
	);
}
